import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import AuthService from '../services/AuthService';
import { UserDataModel as UserData } from '../../models/UserDataModel';
import { useNavigate } from 'react-router-dom';
import iapService from '../../services/IapService';

interface AuthContextProps {
  getUserDataFromToken: (navigate: (path: string) => void, forceRefresh: boolean) => Promise<UserData | null>;
  userData: UserData | null;
  login: (token: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoggedIn: () => Promise<boolean>;
  isLoggedInState: boolean;
  getRole: () => Promise<string | null>;
  hasRole: (role: string) => Promise<boolean>;
  getToken: () => Promise<string | null>;
  getRefreshToken: () => Promise<string | null>;
  setInstagramFacebookAccessToken: (token: string) => Promise<void>;
  getInstagramFacebookAccessToken: () => Promise<string | null>;
  removeInstagramFacebookAccessToken: () => Promise<void>;
  setFacebookAccessToken: (token: string) => Promise<void>;
  getFacebookAccessToken: () => Promise<string | null>;
  removeFacebookAccessToken: () => Promise<void>;
  setIsAllowLoggedInFromHome: (value: boolean) => void;
  isAllowLoggedInFromHome: boolean;
  showGoogleAdsense: boolean;
  setShowGoogleAdsense: (value: boolean) => void;
  isCampaignViewPage: boolean;
  setIsCampaignViewPage: (value: boolean) => void;
  setIsPolingPaymentFired: (value: boolean) => void;
  isPolingPaymentFired: boolean;
  showCookies: boolean;
  setShowCookies: (value: boolean) => void;
  isGoogleProviderReady: boolean;
  setIsGoogleProviderReady: (value: boolean) => void;
  consentVersion: number;
  bumpConsentVersion: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoggedInState, setIsLoggedInState] = useState<boolean>(false);
  const [tokenState, setTokenState] = useState<string | null>();
  const [showCookies, setShowCookies] = useState<boolean>(false);
  const [isAllowLoggedInFromHome, setIsAllowLoggedInFromHome] = useState<boolean>(false);
  const [showGoogleAdsense, setShowGoogleAdsense] = useState<boolean>(false);
  const [isCampaignViewPage, setIsCampaignViewPage] = useState<boolean>(false);
  const [isPolingPaymentFired, setIsPolingPaymentFired] = useState<boolean>(false);
  const [isGoogleProviderReady, setIsGoogleProviderReady] = useState<boolean>(false);
  const [consentVersion, setConsentVersion] = useState<number>(0);

  const bumpConsentVersion = useCallback(() => setConsentVersion(v => v + 1), []);

  const authServiceRef = useRef(AuthService());
  const authService = authServiceRef.current;
  const navigate = useNavigate();

  const getUserDataFromToken = useCallback(async (navigate: (path: string) => void, forceRefresh: boolean = false) => {
    const refreshedUserData = await authService.getUserDataFromToken(navigate, forceRefresh);

    setUserData(refreshedUserData);
    setIsLoggedInState(refreshedUserData != null);

    if (refreshedUserData?.Id) {
      iapService.logIn(refreshedUserData.Id.toString());
    } else {
      iapService.logOut();
    }

    return refreshedUserData;
  }, []);

  const login = useCallback(async (token: string, refreshToken: string) => {
    await authService.login(token, refreshToken);

    setTokenState(await authService.getToken());
  }, []);

  const logout = useCallback(async () => {
    await authService.logout(navigate);

    setTokenState(await authService.getToken());
    iapService.logOut();
  }, [navigate]);

  const isLoggedIn = useCallback(async () => {
    const loggedIn = await authService.isLoggedIn();

    setIsLoggedInState(loggedIn);

    return loggedIn;
  }, []);

  const getRole = useCallback(async () => {
    return authService.getRole();
  }, []);

  const hasRole = useCallback(async (role: string) => {
    return authService.hasRole(role);
  }, []);

  const getToken = useCallback(async () => {
    return authService.getToken();
  }, []);

  const getRefreshToken = useCallback(async () => {
    return authService.getRefreshToken();
  }, []);

  const setInstagramFacebookAccessToken = useCallback(async (facebookAccessToken: string) => {
    await authService.setInstagramFacebookAccessToken(facebookAccessToken);
  }, []);

  const getInstagramFacebookAccessToken = useCallback(async () => {
    return authService.getInstagramFacebookAccessToken();
  }, []);

  const removeInstagramFacebookAccessToken = useCallback(async () => {
    return authService.removeInstagramFacebookAccessToken();
  }, []);

  const setFacebookAccessToken = useCallback(async (facebookAccessToken: string) => {
    await authService.setFacebookAccessToken(facebookAccessToken);
  }, []);

  const getFacebookAccessToken = useCallback(async () => {
    return authService.getFacebookAccessToken();
  }, []);

  const removeFacebookAccessToken = useCallback(async () => {
    return authService.removeFacebookAccessToken();
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const userData = await getUserDataFromToken(navigate);

      setUserData(userData);
      setIsLoggedInState(userData != null);
    };

    initAuth();
  }, [tokenState, getUserDataFromToken, navigate]);

  const value = useMemo(() => ({
    getUserDataFromToken,
    userData,
    login,
    logout,
    isLoggedIn,
    isLoggedInState,
    getRole,
    hasRole,
    getToken,
    getRefreshToken,
    getInstagramFacebookAccessToken,
    setInstagramFacebookAccessToken,
    removeInstagramFacebookAccessToken,
    getFacebookAccessToken,
    setFacebookAccessToken, removeFacebookAccessToken,
    setIsAllowLoggedInFromHome,
    showCookies,
    setShowCookies,
    isAllowLoggedInFromHome,
    showGoogleAdsense,
    setShowGoogleAdsense,
    isCampaignViewPage,
    setIsCampaignViewPage,
    isPolingPaymentFired,
    setIsPolingPaymentFired,
    isGoogleProviderReady,
    setIsGoogleProviderReady,
    consentVersion,
    bumpConsentVersion
  }), [
    getUserDataFromToken,
    userData,
    login,
    logout,
    isLoggedIn,
    isLoggedInState,
    getRole,
    hasRole,
    getToken,
    getRefreshToken,
    getInstagramFacebookAccessToken,
    setInstagramFacebookAccessToken,
    removeInstagramFacebookAccessToken,
    getFacebookAccessToken,
    setFacebookAccessToken,
    removeFacebookAccessToken,
    setIsAllowLoggedInFromHome,
    showCookies,
    setShowCookies,
    isAllowLoggedInFromHome,
    showGoogleAdsense,
    setShowGoogleAdsense,
    isCampaignViewPage,
    setIsCampaignViewPage,
    isPolingPaymentFired,
    setIsPolingPaymentFired,
    isGoogleProviderReady,
    setIsGoogleProviderReady,
    consentVersion,
    bumpConsentVersion,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;